import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../api';


const COLORS = ['Black', 'Off White', 'Blue', 'Maroon', 'Beige', 'Pink'];
const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

const statusBadge = (status) => {
  const map = {
    requested: 'badge-yellow',
    pickup_booked: 'badge-blue',
    in_transit: 'badge-blue',
    received: 'badge-yellow',
    restocked: 'badge-green'
  };
  return <span className={`badge ${map[status] || 'badge-blue'}`}>
    {status.replace('_', ' ')}
  </span>;
};

// Exchanges customers started on the website (pm-inventory
// routes/exchangePortal.js): what they want back, and whether they still owe
// a price difference — no pickup is booked until they have paid it.
const exchangeLabel = (ex) => `${ex.title}${ex.variantTitle ? ' — ' + ex.variantTitle : ''}`;

const ExchangeNote = ({ r }) => {
  const ex = r.exchange;
  if (!ex || !ex.source) return null;
  const pay = ex.paymentStatus === 'pending'
    ? { text: `⏳ awaiting ₹${ex.difference} payment`, color: '#b06d00' }
    : ex.paymentStatus === 'paid'
      ? { text: `✓ ₹${ex.difference} difference paid`, color: '#2e7d50' }
      : null;
  const wants = ex.kind === 'store_credit'
    ? `💳 Wants store credit (₹${ex.creditAmount} after the ₹130 fee)`
    : `🔁 Wants ${exchangeLabel(ex)}`;
  return (
    <div style={{ fontSize: '12px', marginTop: '2px' }}>
      {ex.ref && <span style={{ fontFamily: 'monospace', color: '#7d6a63', marginRight: '6px' }}>{ex.ref}</span>}
      <span style={{ color: '#8c3242', fontWeight: 600 }}>{wants}</span>
      {pay && <span style={{ color: pay.color, marginLeft: '6px' }}>{pay.text}</span>}
    </div>
  );
};

// The pipeline is a board of three columns: exchanges, store-credit returns,
// and everything finished. A return with no decision yet (logged by hand, no
// "exchange" in its reason) sits with store credit — that is QC's default —
// and can still be settled by a replacement from there.
const DONE = ['restocked', 'fulfilled', 'cancelled'];
const isWebExchange = (r) => !!(r.exchange && r.exchange.source && r.exchange.kind !== 'store_credit');
const isExchangeReturn = (r) =>
  isWebExchange(r) || r.resolution === 'replacement' || /exchange|replace/i.test(r.returnReason || '');
const daysSince = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '');

// Lower = needs someone sooner. Ties go to whoever has waited longest.
const urgency = (group) => {
  const r = group[0];
  if (r.status === 'received') return 0;                       // at the office: QC it
  if (r.status === 'requested') {
    if (group.some(g => g.exchange && g.exchange.paymentStatus === 'pending')) return 3; // customer's move
    return 1;                                                  // pickup not booked
  }
  return 2;                                                    // with the courier
};

const nextStep = (group) => {
  const r = group[0];
  if (r.status === 'received') return { text: `Back at the office ${fmtDate(r.receivedAt)} — do QC`, color: '#b3261e' };
  if (r.status === 'requested') {
    const owed = group.find(g => g.exchange && g.exchange.paymentStatus === 'pending');
    if (owed) return { text: `Waiting for the customer to pay ₹${owed.exchange.difference}`, color: '#b06d00' };
    if (r.autoBook && r.autoBook.lastError) return { text: 'Pickup failed to book — book it by hand', color: '#b3261e' };
    return { text: 'Pickup not booked yet', color: '#b3261e' };
  }
  const d = daysSince(r.pickupBookedAt);
  return { text: `Coming back with the courier${d !== null ? ` · day ${d}` : ''}`, color: d > 10 ? '#b06d00' : '#7d6a63' };
};

const outcome = (r) => {
  if (r.status === 'cancelled') return 'Cancelled';
  if (r.returnReason === 'RTO') return 'RTO — back in stock';
  if (r.resolution === 'replacement') return `Replaced with ${r.replacementSize || 'another size'}`;
  if (r.refundAmount > 0) return `Store credit ₹${r.refundAmount}`;
  return 'Back in stock';
};

export default function Returns() {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllDone, setShowAllDone] = useState(false);
  const [search, setSearch] = useState('');
  const [bookingId, setBookingId] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [form, setForm] = useState({
    orderId: '', designCode: '', designName: '',
    color: 'Black', size: 'M', customerName: '', orderValue: ''
  });
  const [adding, setAdding] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [view, setView] = useState('pipeline'); // 'pipeline' | 'inventory' | 'analytics'
  const [invSearch, setInvSearch] = useState('');
  const [usingKey, setUsingKey] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const fetchAnalytics = async () => {
    setAnalytics(null);
    try {
      setAnalytics((await axios.get(`${API}/api/manifest/rto-analytics`)).data);
    } catch (e) { console.error(e); setAnalytics({ error: true }); }
  };

  // Group restocked returns into an available-stock list
  const inventory = Object.values(
    returns.filter(r => r.status === 'restocked').reduce((acc, r) => {
      const key = `${r.designCode}|${r.color}|${r.size}`;
      if (!acc[key]) acc[key] = { designCode: r.designCode, designName: r.designName, color: r.color, size: r.size, qty: 0 };
      acc[key].qty += 1;
      return acc;
    }, {})
  ).sort((a, b) => (a.designName || '').localeCompare(b.designName || ''));
  const inventoryFiltered = inventory.filter(i =>
    !invSearch ||
    (i.designName || '').toLowerCase().includes(invSearch.toLowerCase()) ||
    (i.designCode || '').toLowerCase().includes(invSearch.toLowerCase())
  );
  const inventoryTotal = inventory.reduce((s, i) => s + i.qty, 0);

  useEffect(() => {
    fetchReturns();
  }, []);

  const fetchReturns = async () => {
    try {
      const res = await axios.get(`${API}/api/returns`);
      setReturns(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await axios.post(`${API}/api/returns/add`, {
        ...form,
        orderValue: parseFloat(form.orderValue)
      });
      await fetchReturns();
      setForm({
        orderId: '', designCode: '', designName: '',
        color: 'Black', size: 'M', customerName: '', orderValue: ''
      });
    } catch (e) {
      alert('Error creating return');
    } finally {
      setAdding(false);
    }
  };

  const handleStatusUpdate = async (returnId, newStatus) => {
    setUpdatingId(returnId);
    try {
      await axios.post(`${API}/api/returns/status`, {
        returnId,
        status: newStatus
      });
      await fetchReturns();
    } catch (e) {
      alert('Error updating status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleApproveAndBook = async (returnId) => {
    setBookingId(returnId);
    setBookingResult(null);
    try {
      const res = await axios.post(`${API}/api/shopify/approve-and-book/${returnId}`);
      setBookingResult({
        returnId,
        success: true,
        waybill: `${res.data.waybill} (${res.data.reverseOrderId}) — ${res.data.trackingNote}`
      });
      await fetchReturns();
    } catch (e) {
      setBookingResult({ returnId, success: false, error: e.response?.data?.message || e.message });
    } finally {
      setBookingId(null);
    }
  };

  // QC an exchange and release what goes back out, in one press. Nothing is
  // put on the order to pack until the returned piece has passed QC — the
  // replacement is created here, then the piece is shelved.
  const handleQcAndReplace = async (r) => {
    const ex = isWebExchange(r) ? r.exchange : null;
    const head = `QC passed for ${r.designName} (${r.color} ${r.size}), order ${r.shopifyOrderNumber || r.orderId}?`;
    let size = '';
    if (ex) {
      if (!window.confirm(
        `${head}\n\nSending: ${exchangeLabel(ex)}\n\n` +
        `It goes onto the Manifest tab to pack. No store credit.`
      )) return;
    } else {
      size = window.prompt(`${head}\n\nWhich size should go out as the replacement?`, '');
      if (!size || !size.trim()) return;
    }
    setUpdatingId(r._id);
    try {
      const { data } = await axios.post(`${API}/api/returns/send-replacement/${r._id}`, ex ? {} : { size: size.trim() });
      await axios.post(`${API}/api/returns/qc-restock/${r._id}`);
      alert(
        `✓ QC done — returned piece is on the shelf.\n✓ ${data.message}\n\n` +
        `Shopify: ${data.shopify.ok ? 'noted on order ' + data.orderNumber + ' ✓' : '⚠ could not note it — ' + data.shopify.reason}\n\n` +
        `Pack it from the Manifest tab.`
      );
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    } finally {
      await fetchReturns();
      setUpdatingId(null);
    }
  };

  // QC on a return already settled by a replacement: shelve it, pay nothing.
  const handleQcNoCredit = async (r) => {
    if (!window.confirm(
      `QC passed for ${r.designName} (${r.color} ${r.size}), order ${r.shopifyOrderNumber || r.orderId}?\n\n` +
      `A replacement ${r.replacementSize || ''} was already sent, so NO store credit will be issued.\n` +
      `The piece goes onto the returns shelf.`
    )) return;
    setUpdatingId(r._id);
    try {
      const { data } = await axios.post(`${API}/api/returns/qc-restock/${r._id}`);
      alert('✓ ' + data.message);
      await fetchReturns();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRefundStoreCredit = async (r) => {
    const est = r.orderValue ? `₹${(r.orderValue - 130).toFixed(2)}` : 'order total minus ₹130';
    if (!window.confirm(
      `QC passed for ${r.designName} (${r.color} ${r.size}), order ${r.shopifyOrderNumber || r.orderId}?\n\nThis will give the customer ${est} as STORE CREDIT and add the item to returns inventory.`
    )) return;
    setUpdatingId(r._id);
    try {
      const res = await axios.post(`${API}/api/shopify/refund-store-credit/${r._id}`);
      const d = res.data;
      // Show the Shopify side too — a return left open, or a balance still
      // showing as owed, used to pass silently.
      alert(
        `✓ Store credit of ₹${d.storeCreditIssued} issued (paid ₹${d.customerPaid} − ₹${d.restockingFee} fee).\n` +
        `Item added to returns inventory.\n\n` +
        `Shopify return: ${d.shopifyReturnClosed ? 'closed ✓' : '⚠ ' + d.shopifyNote}\n` +
        `Refund balance: ${d.refundSettled}`
      );
      await fetchReturns();
    } catch (e) {
      alert('Refund failed: ' + (e.response?.data?.message || JSON.stringify(e.response?.data?.errors) || e.message));
    } finally {
      setUpdatingId(null);
    }
  };

  // Use one returned piece to ship an already-manifested order: discard it
  // from returns inventory and put its blank + print back into stock.
  const handleUseForManifested = async (item) => {
    if (!window.confirm(
      `Use 1 × ${item.designName} (${item.color} ${item.size}) from returns to ship an already-manifested order?\n\n` +
      `This removes it from returns inventory and puts +1 ${item.color} ${item.size} blank and +1 ${item.designCode} print back into stock.`
    )) return;
    setUsingKey(`${item.designCode}-${item.color}-${item.size}`);
    try {
      const res = await axios.post(`${API}/api/returns/use-for-manifested`, {
        designCode: item.designCode, color: item.color, size: item.size
      });
      alert('✓ ' + res.data.message);
      await fetchReturns();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setUsingKey(null);
    }
  };

  const handleCancelReturn = async (group) => {
    const items = Array.isArray(group) ? group : [group];
    if (!window.confirm(`Cancel this return request (${items.length} item(s))? It will be hidden from the pending list.`)) return;
    try {
      for (const item of items) {
        await axios.post(`${API}/api/returns/cancel/${item._id}`);
      }
      await fetchReturns();
    } catch (e) {
      alert('Cancel failed: ' + (e.response?.data?.message || e.message));
    }
  };

  // Items of the same Shopify return are shown and handled as ONE return
  const groupByReturn = (list) => Object.values(
    list.reduce((acc, r) => {
      const k = r.shopifyReturnId || r._id;
      (acc[k] = acc[k] || []).push(r);
      return acc;
    }, {})
  );
  // A whole return matches if ANY of its items does, so searching a design
  // still surfaces the multi-item return it belongs to.
  const matchesSearch = (group) => {
    const q = search.trim().toLowerCase().replace(/#/g, '');
    if (!q) return true;
    const hay = group.map(r => [
      r.shopifyOrderNumber, r.orderId, r.customerName,
      r.designName, r.designCode, r.color, r.size,
      r.delhiveryWaybill, r.status, String(r.status || '').replace(/_/g, ' '),
      String(r.returnReason || '').replace(/_/g, ' ')
    ].join(' ')).join(' ').toLowerCase().replace(/#/g, '');
    return hay.includes(q);
  };

  const allGroups = groupByReturn(returns);
  const shownGroups = allGroups.filter(matchesSearch);
  const byUrgency = (a, b) =>
    urgency(a) - urgency(b) || new Date(a[0].createdAt) - new Date(b[0].createdAt);
  const isDone = (group) => group.every(r => DONE.includes(r.status));
  const exchangeGroups = shownGroups.filter(g => !isDone(g) && g.some(isExchangeReturn)).sort(byUrgency);
  const creditGroups = shownGroups.filter(g => !isDone(g) && !g.some(isExchangeReturn)).sort(byUrgency);
  const doneGroups = shownGroups.filter(isDone).sort((a, b) =>
    new Date(b[0].restockedAt || b[0].updatedAt) - new Date(a[0].restockedAt || a[0].updatedAt));
  const DONE_SHOWN = 25;

  const requested = returns.filter(r => r.status === 'requested').length;
  const inTransit = returns.filter(r => r.status === 'in_transit' || r.status === 'pickup_booked').length;
  const received = returns.filter(r => r.status === 'received').length;
  const restocked = returns.filter(r => r.status === 'restocked').length;

  const smallBtn = {
    fontSize: '11px', padding: '4px 10px', background: '#ffffff', color: '#8c3242',
    border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer'
  };

  // One return (all its items) as a card on the board.
  const renderReturnCard = (group, done) => {
    const r = group[0];
    const step = done ? null : nextStep(group);
    const busy = group.some(g => updatingId === g._id) || bookingId === r._id;
    const paymentPending = group.some(g => g.exchange && g.exchange.paymentStatus === 'pending');
    const wantsCredit = group.some(g => g.exchange && g.exchange.kind === 'store_credit');
    // Store credit is settled for the whole return at once; replacements go
    // item by item, because each item can want something different back.
    const canCredit = r.status === 'received' && !group.some(g => isWebExchange(g) || g.resolution === 'replacement');
    return (
      <div key={r._id} style={{
        background: '#ffffff', border: '1px solid #e6dccc', borderRadius: '8px',
        padding: '10px 12px', marginBottom: '10px', opacity: done ? 0.85 : 1
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'baseline' }}>
          <div>
            <span style={{ fontFamily: 'monospace', color: '#8c3242', fontWeight: 600 }}>#{r.shopifyOrderNumber || r.orderId}</span>
            <span style={{ color: '#2e1f23', marginLeft: '8px' }}>{r.customerName}</span>
          </div>
          {done
            ? <span style={{ fontSize: '11px', color: '#93827a', whiteSpace: 'nowrap' }}>{fmtDate(r.restockedAt || r.updatedAt)}</span>
            : statusBadge(r.status)}
        </div>

        {step && <div style={{ fontSize: '12px', fontWeight: 600, color: step.color, margin: '4px 0 2px' }}>{step.text}</div>}
        {done && <div style={{ fontSize: '12px', fontWeight: 600, color: r.status === 'cancelled' ? '#93827a' : '#2e7d50', margin: '4px 0 2px' }}>{outcome(r)}</div>}

        {group.map(item => (
          <div key={item._id} style={{ fontSize: '13px', marginTop: '6px' }}>
            <span style={{ fontWeight: 600 }}>{item.designName}</span>
            <span style={{ color: '#7d6a63' }}> · {item.color} · {item.size}</span>
            {!done && item.returnReason && <span style={{ color: '#93827a', fontSize: '12px' }}> · {item.returnReason.replace(/_/g, ' ')}</span>}
            {!done && <ExchangeNote r={item} />}
            {!done && item.resolution === 'replacement' && (
              <div style={{ fontSize: '12px', color: '#2e7d50' }}>replacement {item.replacementSize} decided</div>
            )}

            {/* QC for exchanges, per item. Pressing it is what releases the
                replacement to the Manifest tab. */}
            {item.status === 'received' && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                {item.resolution === 'replacement' ? (
                  <button className="btn-primary" style={{ fontSize: '12px', padding: '6px 12px' }}
                    disabled={busy} onClick={() => handleQcNoCredit(item)}
                    title={`Replacement ${item.replacementSize || ''} already decided — no store credit is due`}>
                    {updatingId === item._id ? '...' : 'QC OK → shelve (no credit)'}
                  </button>
                ) : isWebExchange(item) ? (
                  <button className="btn-primary" style={{ fontSize: '12px', padding: '6px 12px' }}
                    disabled={busy} onClick={() => handleQcAndReplace(item)}
                    title={'Sends what the customer chose: ' + exchangeLabel(item.exchange)}>
                    {updatingId === item._id ? '...' : `QC OK → send ${item.exchange.variantTitle || 'their pick'}`}
                  </button>
                ) : !wantsCredit && (
                  <button
                    className={isExchangeReturn(item) ? 'btn-primary' : undefined}
                    style={isExchangeReturn(item) ? { fontSize: '12px', padding: '6px 12px' } : smallBtn}
                    disabled={busy} onClick={() => handleQcAndReplace(item)}
                    title="QC the returned piece and send this design again in another size">
                    {updatingId === item._id ? '...' : 'QC OK → send replacement'}
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        {!done && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px', alignItems: 'center' }}>
            {canCredit && (
              <button
                className={group.some(isExchangeReturn) ? undefined : 'btn-primary'}
                style={group.some(isExchangeReturn) ? smallBtn : { fontSize: '12px', padding: '6px 12px' }}
                disabled={busy} onClick={() => handleRefundStoreCredit(r)}>
                {updatingId === r._id ? '...' : 'QC OK → Store Credit'}
              </button>
            )}
            {r.status === 'requested' && (<>
              <button className="btn-primary" style={{ fontSize: '12px', padding: '6px 12px' }}
                onClick={() => handleApproveAndBook(r._id)}
                disabled={busy || paymentPending}
                title={paymentPending ? 'Waiting for the customer to pay the exchange difference' : ''}>
                {bookingId === r._id ? 'Working...' : '✓ Approve & Book Pickup'}
              </button>
              <button className="btn-danger" style={{ fontSize: '12px', padding: '6px 12px' }}
                onClick={() => handleCancelReturn(group)}>
                Cancel
              </button>
            </>)}
            {(r.status === 'pickup_booked' || r.status === 'in_transit') && (
              <button style={{ ...smallBtn, color: '#7d6a63' }} disabled={busy}
                onClick={async () => { for (const item of group) await handleStatusUpdate(item._id, 'received'); }}
                title="Tracking updates this automatically — use only if the parcel arrived but the status didn't move">
                {updatingId === r._id ? '...' : 'Mark received'}
              </button>
            )}
            {r.delhiveryWaybill && (
              <span style={{ fontSize: '11px', color: '#93827a', fontFamily: 'monospace' }}>{r.delhiveryWaybill}</span>
            )}
          </div>
        )}
        {!done && r.autoBook && r.autoBook.lastError && (
          <div style={{ fontSize: '11px', color: '#b3261e', marginTop: '6px' }}>
            Auto-booking failed{r.autoBook.attempts >= 3 ? ' 3 times — book by hand' : ` (try ${r.autoBook.attempts} of 3, retrying)`}: {r.autoBook.lastError}
          </div>
        )}
        {bookingResult && bookingResult.returnId === r._id && (
          <div style={{ fontSize: '12px', marginTop: '6px', color: bookingResult.success ? '#2e7d50' : '#b3261e' }}>
            {bookingResult.success ? `Pickup booked — waybill ${bookingResult.waybill}` : `Booking failed: ${bookingResult.error}`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Requested</div>
          <div className="stat-value" style={{ color: '#7a6a12' }}>{requested}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">In Transit</div>
          <div className="stat-value" style={{ color: '#8c3242' }}>{inTransit}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Received</div>
          <div className="stat-value" style={{ color: '#7a6a12' }}>{received}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Restocked (Returns Inventory)</div>
          <div className="stat-value" style={{ color: '#2e7d50' }}>{restocked}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {[['pipeline', 'Returns Pipeline'], ['inventory', `Returns Inventory (${inventoryTotal})`], ['analytics', 'RTO & Returns Analytics']].map(([v, label]) => (
          <button
            key={v}
            onClick={() => { setView(v); if (v === 'analytics' && !analytics) fetchAnalytics(); }}
            style={{
              padding: '8px 16px', fontSize: '14px', borderRadius: '8px', cursor: 'pointer',
              background: view === v ? '#6e1f2a' : '#ffffff',
              color: view === v ? '#fff' : '#7d6a63',
              border: `1px solid ${view === v ? '#6e1f2a' : '#e6dccc'}`
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'analytics' && (
        !analytics ? <div className="card"><p style={{ color: '#93827a' }}>Loading analytics...</p></div> :
        analytics.error ? <div className="card"><p style={{ color: '#b3261e' }}>Failed to load analytics.</p></div> : (
        <>
          <div className="stat-grid" style={{ marginBottom: '18px' }}>
            <div className="stat-card"><div className="stat-label">Total RTO</div><div className="stat-value" style={{ color: '#b3261e' }}>{analytics.rto.totalRto}</div></div>
            <div className="stat-card"><div className="stat-label">Avg RTO transit (turn → origin)</div><div className="stat-value" style={{ color: '#7a6a12' }}>{analytics.rto.avgTransitDays ?? '—'}<span style={{ fontSize: '14px', color: '#7d6a63' }}> days</span></div></div>
            <div className="stat-card"><div className="stat-label">Reverse pickups booked</div><div className="stat-value" style={{ color: '#8c3242' }}>{analytics.reversePickups.totalBooked}</div></div>
            <div className="stat-card"><div className="stat-label">Avg pickup → received</div><div className="stat-value" style={{ color: '#7a6a12' }}>{analytics.reversePickups.avgDays ?? '—'}<span style={{ fontSize: '14px', color: '#7d6a63' }}> days</span></div></div>
          </div>

          <div className="card">
            <div className="card-title">RTO — forward shipments that came back</div>
            <p style={{ fontSize: '12px', color: '#93827a', marginBottom: '12px' }}>
              {analytics.rto.completed} of {analytics.rto.totalRto} completed the return trip (avg {analytics.rto.avgTransitDays ?? '—'} days from turning RTO to reaching origin); {analytics.rto.inProgress} still on the way back. Timings come from Delhivery scan history.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead><tr><th>City</th><th>RTO count</th><th>Avg transit (days)</th><th>Samples</th></tr></thead>
                <tbody>
                  {analytics.rto.byCity.map(c => (
                    <tr key={'rto-' + c.city}>
                      <td style={{ fontWeight: 600 }}>{c.city}</td>
                      <td style={{ color: c.rtoCount >= 3 ? '#b3261e' : '#2e1f23', fontWeight: 600 }}>{c.rtoCount}</td>
                      <td>{c.avgTransitDays ?? '—'}</td>
                      <td style={{ color: '#93827a' }}>{c.samples}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {analytics.rto.exceptions.length > 0 && (
              <div style={{ marginTop: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#b3261e', marginBottom: '6px' }}>⚠ Exceptions — over {analytics.slaThresholds.rtoDays} days</div>
                {analytics.rto.exceptions.map((e, i) => (
                  <div key={i} style={{ fontSize: '13px', color: '#4a3840', padding: '2px 0' }}>
                    <span style={{ fontFamily: 'monospace', color: '#8c3242' }}>{e.orderNumber}</span> · {e.city} · {e.transitDays ? `${e.transitDays}d` : `${e.daysSinceRto}d (still returning)`}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-title">Reverse pickups — customer returns picked up</div>
            <p style={{ fontSize: '12px', color: '#93827a', marginBottom: '12px' }}>
              {analytics.reversePickups.received} of {analytics.reversePickups.totalBooked} pickups received back (avg {analytics.reversePickups.avgDays ?? '—'} days from booking to arrival).
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead><tr><th>City</th><th>Pickups</th><th>Avg days</th><th>Samples</th></tr></thead>
                <tbody>
                  {analytics.reversePickups.byCity.map(c => (
                    <tr key={'rev-' + c.city}>
                      <td style={{ fontWeight: 600 }}>{c.city}</td>
                      <td style={{ fontWeight: 600 }}>{c.pickups}</td>
                      <td>{c.avgDays ?? '—'}</td>
                      <td style={{ color: '#93827a' }}>{c.samples}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {analytics.reversePickups.exceptions.length > 0 && (
              <div style={{ marginTop: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#b3261e', marginBottom: '6px' }}>⚠ Exceptions — over {analytics.slaThresholds.reverseDays} days</div>
                {analytics.reversePickups.exceptions.map((e, i) => (
                  <div key={i} style={{ fontSize: '13px', color: '#4a3840', padding: '2px 0' }}>
                    <span style={{ fontFamily: 'monospace', color: '#8c3242' }}>{e.orderNumber}</span> · {e.city} · {e.days ? `${e.days}d` : `${e.daysSincePickup}d (still in transit)`}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ))}

      {view === 'inventory' && (
        <div className="card">
          <div className="card-title">Returns Inventory — Available Restocked Items</div>
          <p style={{ fontSize: '13px', color: '#7d6a63', marginBottom: '16px' }}>
            These returned pieces are back in stock and are used <b>first</b> when a new order needs the same design/colour/size.
          </p>
          <input
            placeholder="Search design name or code..."
            value={invSearch}
            onChange={e => setInvSearch(e.target.value)}
            style={{ width: '300px', marginBottom: '16px' }}
          />
          <table>
            <thead>
              <tr><th>Code</th><th>Design</th><th>Colour</th><th>Size</th><th>Qty in Returns Stock</th><th>Action</th></tr>
            </thead>
            <tbody>
              {inventoryFiltered.map(i => {
                const key = `${i.designCode}-${i.color}-${i.size}`;
                return (
                <tr key={key}>
                  <td style={{ fontFamily: 'monospace', color: '#8c3242' }}>{i.designCode}</td>
                  <td style={{ fontWeight: 500 }}>{i.designName}</td>
                  <td>{i.color}</td>
                  <td>{i.size}</td>
                  <td style={{ fontWeight: 700, color: '#2e7d50' }}>{i.qty}</td>
                  <td>
                    <button
                      onClick={() => handleUseForManifested(i)}
                      disabled={usingKey === key}
                      title={`Ship this returned piece in an already-manifested order. Removes 1 from returns and adds +1 ${i.color} ${i.size} blank and +1 ${i.designCode} print back to stock.`}
                      style={{
                        fontSize: '12px', padding: '5px 11px',
                        background: '#ffffff', color: '#8c3242',
                        border: '1px solid #e6dccc', borderRadius: '6px',
                        cursor: 'pointer', whiteSpace: 'nowrap'
                      }}
                    >
                      {usingKey === key ? 'Working...' : '↔ Use for manifested order'}
                    </button>
                  </td>
                </tr>
                );
              })}
              {inventoryFiltered.length === 0 && (
                <tr><td colSpan="6" style={{ color: '#93827a', textAlign: 'center' }}>
                  {inventory.length === 0 ? 'No items in returns inventory yet' : 'No matches'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {view === 'pipeline' && (<>
      <div className="card">
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            placeholder="Search order #, customer, design, code, colour, size, waybill..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', maxWidth: '420px' }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                fontSize: '12px', padding: '6px 12px', background: '#ffffff',
                color: '#7d6a63', border: '1px solid #e6dccc',
                borderRadius: '6px', cursor: 'pointer'
              }}
            >
              Clear
            </button>
          )}
          <span style={{ fontSize: '12px', color: '#93827a' }}>
            Website requests book their own pickup. Each column puts what needs you first at the top.
          </span>
        </div>
      </div>

      {loading ? <div className="card"><p style={{ color: '#93827a' }}>Loading...</p></div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', alignItems: 'start', marginBottom: '20px' }}>
          {[
            ['🔁 Exchanges', exchangeGroups, 'No open exchanges'],
            ['💳 Returns — store credit', creditGroups, 'No open store-credit returns'],
            ['✓ Completed', showAllDone ? doneGroups : doneGroups.slice(0, DONE_SHOWN), 'Nothing completed yet']
          ].map(([title, groups, empty], col) => (
            <div key={title} style={{ background: '#f8f3eb', border: '1px solid #e6dccc', borderRadius: '10px', padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
                <div style={{ fontWeight: 700, color: '#2e1f23', fontSize: '15px' }}>{title}</div>
                <div style={{ fontSize: '12px', color: '#93827a' }}>{col === 2 ? doneGroups.length : groups.length}</div>
              </div>
              {groups.map(group => renderReturnCard(group, col === 2))}
              {groups.length === 0 && (
                <div style={{ fontSize: '13px', color: '#93827a', textAlign: 'center', padding: '16px 0' }}>
                  {search ? `No matches for "${search}"` : empty}
                </div>
              )}
              {col === 2 && doneGroups.length > DONE_SHOWN && (
                <button
                  onClick={() => setShowAllDone(!showAllDone)}
                  style={{
                    width: '100%', fontSize: '12px', padding: '6px', background: '#ffffff',
                    color: '#7d6a63', border: '1px solid #e6dccc', borderRadius: '6px', cursor: 'pointer'
                  }}
                >
                  {showAllDone ? 'Show recent only' : `Show all ${doneGroups.length}`}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-title">Log New Return</div>
        <form onSubmit={handleAdd} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Order ID</div>
            <input
              placeholder="Shopify Order ID"
              value={form.orderId}
              onChange={e => setForm({ ...form, orderId: e.target.value })}
              style={{ width: '100%' }}
              required
            />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Customer Name</div>
            <input
              placeholder="Customer Name"
              value={form.customerName}
              onChange={e => setForm({ ...form, customerName: e.target.value })}
              style={{ width: '100%' }}
              required
            />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Design Code</div>
            <input
              placeholder="e.g. KJ001"
              value={form.designCode}
              onChange={e => setForm({ ...form, designCode: e.target.value })}
              style={{ width: '100%' }}
              required
            />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Design Name</div>
            <input
              placeholder="e.g. King James"
              value={form.designName}
              onChange={e => setForm({ ...form, designName: e.target.value })}
              style={{ width: '100%' }}
              required
            />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Color</div>
            <select
              value={form.color}
              onChange={e => setForm({ ...form, color: e.target.value })}
              style={{ width: '100%' }}
            >
              {COLORS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Size</div>
            <select
              value={form.size}
              onChange={e => setForm({ ...form, size: e.target.value })}
              style={{ width: '100%' }}
            >
              {SIZES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#93827a', marginBottom: '6px' }}>Order Value (₹)</div>
            <input
              type="number"
              placeholder="e.g. 999"
              value={form.orderValue}
              onChange={e => setForm({ ...form, orderValue: e.target.value })}
              style={{ width: '100%' }}
              required
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button type="submit" className="btn-primary" disabled={adding} style={{ width: '100%', padding: '10px' }}>
              {adding ? 'Creating...' : 'Create Return'}
            </button>
          </div>
        </form>
      </div>
      </>)}
    </div>
  );
}
